import ConfirmDialog from '@/components/ConfirmDialog';
import automationFlowsService from '@/services/automationFlowsService';
import { toastService } from '@/services/toastService';
import { format } from 'date-fns';
import { Edit2, Loader2, Plus, Search, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

const FlowList = () => {
  const { projectId } = useParams();
  const [flows, setFlows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [confirmDelete, setConfirmDelete] = useState({
    isOpen: false,
    id: null,
  });

  const fetchFlows = useCallback(async () => {
    try {
      setLoading(true);
      const response = await automationFlowsService.getFlows(projectId, {
        search: searchTerm,
      });
      if (response.success) {
        setFlows(response.data.docs || []);
      }
    } catch (error) {
      console.error('Error fetching flows:', error);
      toastService.showError('Failed to load automation flows');
    } finally {
      setLoading(false);
    }
  }, [projectId, searchTerm]);

  useEffect(() => {
    fetchFlows();
  }, [fetchFlows]);

  const handleDelete = async flowId => {
    setConfirmDelete({ isOpen: true, id: flowId });
  };

  const confirmDeleteFlow = async () => {
    const flowId = confirmDelete.id;
    if (!flowId) return;

    try {
      setDeletingId(flowId);
      const response = await automationFlowsService.deleteFlow(
        flowId,
        projectId
      );
      if (response.success) {
        toastService.showSuccess('Flow deleted successfully');
        setFlows(prev => prev.filter(f => f._id !== flowId));
      }
    } catch (error) {
      console.error('Error deleting flow:', error);
      toastService.showError('Failed to delete flow');
    } finally {
      setDeletingId(null);
      setConfirmDelete({ isOpen: false, id: null });
    }
  };

  return (
    <div className="p-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Automations</h2>
          <p className="text-sm text-gray-500">
            Create and manage your chatbot flows
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
          <div className="relative flex-1 sm:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search flows..."
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>
          <Link
            to={`/projects/${projectId}/flows/create`}
            className="inline-flex items-center justify-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors shadow-sm whitespace-nowrap"
          >
            <Plus className="h-5 w-5 mr-2" />
            Create Flow
          </Link>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-24">
            <Loader2 className="h-10 w-10 text-primary-500 animate-spin mb-4" />
            <p className="text-gray-500 font-medium">Loading flows...</p>
          </div>
        ) : flows.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Flow Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Last Updated
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {flows.map(flow => (
                  <tr
                    key={flow._id}
                    className="hover:bg-gray-50 transition-colors"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {flow.name}
                      </div>
                      {flow.description && (
                        <div className="text-xs text-gray-500 truncate max-w-xs">
                          {flow.description}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          flow.isActive
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {flow.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {format(new Date(flow.updatedAt), 'MMM dd, yyyy')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-3">
                        <Link
                          to={`/projects/${projectId}/flows/${flow._id}`}
                          className="text-primary-600 hover:text-primary-900"
                        >
                          <Edit2 className="h-4 w-4" />
                        </Link>
                        <button
                          onClick={() => handleDelete(flow._id)}
                          disabled={deletingId === flow._id}
                          className="text-red-600 hover:text-red-900 disabled:opacity-50"
                        >
                          {deletingId === flow._id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Trash2 className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-24 text-center px-4">
            <div className="bg-gray-50 p-4 rounded-full mb-4">
              <Search className="h-8 w-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-1">
              No flows found
            </h3>
            <p className="text-gray-500 max-w-sm">
              {searchTerm
                ? `We couldn't find any flows matching "${searchTerm}". Try a different search term.`
                : "You haven't created any automation flows yet. Start by creating your first one!"}
            </p>
            {!searchTerm && (
              <Link
                to={`/projects/${projectId}/flows/create`}
                className="mt-6 inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors shadow-sm"
              >
                <Plus className="h-5 w-5 mr-2" />
                Create Flow
              </Link>
            )}
          </div>
        )}
      </div>

      <ConfirmDialog
        isOpen={confirmDelete.isOpen}
        title="Delete Flow"
        message="Are you sure you want to delete this flow? This action cannot be undone."
        confirmText="Delete Flow"
        confirmVariant="danger"
        isLoading={deletingId !== null}
        onConfirm={confirmDeleteFlow}
        onCancel={() => setConfirmDelete({ isOpen: false, id: null })}
      />
    </div>
  );
};

export default FlowList;
